import { auditFor } from "@/data/audits";
import { hasPreciseSources, sourcesFor } from "@/data/sources";

/**
 * Badge de vérification et indicateurs de fiabilité d'une fiche.
 * Le niveau dépend du nombre de références précises et de l'audit factuel.
 */
export function ReliabilityBadge({
  sourceKey,
  className = "",
}: {
  sourceKey?: string;
  className?: string;
}) {
  if (!sourceKey) return null;
  const sources = sourcesFor(sourceKey);
  const precise = hasPreciseSources(sourceKey);
  const audit = auditFor(sourceKey);
  const publishers = new Set(sources.map((s) => s.publisher)).size;

  const level =
    precise && audit && publishers >= 3
      ? {
          label: "Faits vérifiés",
          tone: "border-foreground bg-foreground text-background",
          hint: "Chaque fait et chiffre est adossé à une source publique consultable.",
        }
      : precise
        ? {
            label: "Sources précises",
            tone: "border-foreground text-foreground",
            hint: "Références directes disponibles pour les principales affirmations.",
          }
        : {
            label: "Fonds documentaire",
            tone: "border-border text-muted-foreground",
            hint: "Page appuyée sur un fonds d'archives général, vérification en cours.",
          };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[0.65rem] uppercase tracking-[0.2em] ${level.tone}`}
        title={level.hint}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="h-3.5 w-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M12 2.5 4.5 5.8v5.4c0 4.6 3.1 8.4 7.5 10.3 4.4-1.9 7.5-5.7 7.5-10.3V5.8z" />
          <path d="m8.8 12.2 2.2 2.2 4.2-4.4" />
        </svg>
        {level.label}
      </span>

      <span className="rounded-full border border-border px-3 py-1 text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
        {sources.length} source{sources.length > 1 ? "s" : ""} · {publishers} média
        {publishers > 1 ? "s" : ""}
      </span>

      {audit ? (
        <span
          className="rounded-full border border-border px-3 py-1 text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground"
          title={audit.method}
        >
          Audité le {audit.date}
        </span>
      ) : null}

      <a
        href="#sources"
        className="text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground underline underline-offset-4 hover:text-foreground"
      >
        Voir les références
      </a>
    </div>
  );
}
